// Wraps zxing-wasm. No decoding logic of our own — this file is lifecycle and result-shape
// mapping only. Ported from event-connect's scanner (decoder/wasm-decoder.ts); the only change
// is the reader loader import (./load-zxing-reader instead of @event-connect/camera-kit, which
// doesn't exist in this repo — see load-zxing-reader.ts for the CDN-vs-self-hosted wasm note).

import { loadZxingReader, type ZxingReaderModule } from './load-zxing-reader'
import { boundingBoxOf, normalizeCorners } from '../geometry'
import { LEAN } from './decode-options'
import type { Decoder, DecoderCapabilities, Detection } from './types'

type ReaderModule = ZxingReaderModule
type ReadInputBarcodeFormat = Awaited<ReturnType<typeof loadReadableFormats>>[number]

async function loadReadableFormats() {
  const { READABLE_BARCODE_FORMATS } = await import('zxing-wasm/reader')
  return READABLE_BARCODE_FORMATS
}

const loadReader = loadZxingReader

export class WasmDecoder implements Decoder {
  readonly name = 'zxing-wasm'
  // TS `private`, not JS `#` private fields — Gatsby's SSR webpack/babel config doesn't have
  // @babel/plugin-transform-private-methods enabled.
  private reader: ReaderModule | null = null
  private formats: ReadInputBarcodeFormat[] = []
  private destroyed = false

  async init(formats: readonly string[]): Promise<DecoderCapabilities> {
    // Re-initialising after a destroy is legitimate and common: the decoder instance is held
    // for the lifetime of the component that owns it, and any remount (StrictMode double-invoke,
    // navigating back) runs cleanup → destroy() and then init() again on the SAME instance.
    // Leaving `destroyed` sticky made that second init() bail at the guard below without ever
    // assigning `reader`, while the scan loop went on to start the frame loop — so every tick
    // threw "detect() called before init()" and the scanner was permanently dead behind a live
    // camera preview. (Found on a real device in event-connect; ported defensively.)
    this.destroyed = false
    const [reader, readable] = await Promise.all([loadReader(), loadReadableFormats()])
    // destroy() can still land while the loads above are in flight (e.g. an unmount racing
    // init()) — don't resurrect `reader` after that, or the wasm module outlives the component
    // that owned it. Resetting the flag on entry above does not weaken this guard: it only
    // covers a destroy that happened BEFORE this init() was called.
    if (this.destroyed) return { supportedFormats: [], unsupportedFormats: [...formats] }
    this.reader = reader

    const readableSet = new Set<string>(readable)
    const supportedFormats = formats.filter(f => readableSet.has(f))
    const unsupportedFormats = formats.filter(f => !readableSet.has(f))
    this.formats = supportedFormats as ReadInputBarcodeFormat[]

    return { supportedFormats, unsupportedFormats }
  }

  async detect(source: HTMLCanvasElement): Promise<Detection[]> {
    if (!this.reader) throw new Error('WasmDecoder.detect() called before init()')
    const context = source.getContext('2d', { willReadFrequently: true })
    if (!context) return []

    const imageData = context.getImageData(0, 0, source.width, source.height)
    const results = await this.reader.readBarcodes(imageData, {
      formats: this.formats,
      // readBarcodes runs synchronously on the calling thread here (no Worker offload for
      // this fallback path), so it always runs the conservative LEAN set — see decode-options.ts.
      ...LEAN,
    })

    return results.filter(r => r.isValid).map(toDetection)
  }

  destroy(): void {
    this.destroyed = true
    this.reader = null
  }
}

function toDetection(result: Awaited<ReturnType<ReaderModule['readBarcodes']>>[number]): Detection {
  const { topLeft, topRight, bottomLeft, bottomRight } = result.position
  const cornerPoints = normalizeCorners([topLeft, topRight, bottomRight, bottomLeft])

  return {
    rawValue: result.text,
    format: result.format,
    cornerPoints,
    boundingBox: boundingBoxOf(cornerPoints),
  }
}
